/**
 * @file useComicVolumeNav.js
 * @description 漫画阅读页 — 上下卷切换与连续加载
 * @module features/comic/composables/useComicVolumeNav
 */

import { fetchVolPagesApi, fetchVolInteractApi } from '../api/comicApi'
import { convertRouteParams } from '@/shared/utils/idHelper'
import { normalizeVolInteractData } from '../utils/normalizeVolInteract'
import { shouldPromptLoginForVol, findVolByNum, VOL_ACCESS_STATUS } from '../utils/volAccess'
import {
  applyLockedVolPreviewImages,
  isVolPagesReadable,
  resolveVolPagesAccess,
  resolveVolPagesResult,
} from '../utils/handleVolPagesResponse'
import { openVolPay } from './useVolPay'

/**
 * @param {Record<string, Function>} bridge 共享方法桥
 * @param {object} ctx reactive 状态
 */
export function useComicVolumeNav(bridge, ctx) {
  function captureVolumeSession() {
    return ctx.pagesFetchToken
  }

  /**
   * 未登录且目标卷 NEED_LOGIN 时拦截
   * @param {number} volNum
   * @returns {boolean}
   */
  function isGuestBlockedVol(volNum) {
    return shouldPromptLoginForVol(ctx.volInfoVos, volNum, Boolean(ctx.token))
  }

  /**
   * 邻卷旧 10701/10702：不拼进阅读流，也不抢开购卷窗。
   * @param {unknown} code
   * @returns {boolean} 是否已处理业务码
   */
  function handleDeniedAdjacentVolPages(code) {
    const result = resolveVolPagesResult(code)
    if (result === 'need-login') {
      ctx.$emit('show-login-popup')
      return true
    }
    if (result === 'need-pay') {
      return true
    }
    return false
  }

  /**
   * 邻卷 200 但未解锁时不拼进阅读流。
   * @param {unknown} data vol/all data
   * @returns {boolean}
   */
  function shouldSkipLockedAdjacentData(data) {
    return !isVolPagesReadable(data, { hasToken: Boolean(ctx.token) })
  }

  function hideLoadVolumePrompt() {
    ctx.showLoadNextVolumePrompt = false
    ctx.showLoadPrevVolumePrompt = false
    ctx.loadVolumePromptText = ''
  }

  /**
   * 滑到未解锁邻卷：未登录只弹登录（留在当前卷）；
   * 已登录则立刻用邻卷已拉到的第一张图做灰度预览并弹购卷窗，再同步路由。
   * 抑制随后的 applyVolumeSwitch，避免清空刚铺上的预览。
   * @param {unknown} data vol/all data
   * @param {number} volNum
   */
  function enterLockedAdjacentVol(data, volNum) {
    hideLoadVolumePrompt()
    const payload = data && typeof data === 'object' ? data : {}
    const access = resolveVolPagesAccess(payload, { hasToken: Boolean(ctx.token) })
    if (access === VOL_ACCESS_STATUS.NEED_LOGIN) {
      ctx.$emit('show-login-popup')
      return
    }

    ctx.scrollTriggeredLoadNext = false
    ctx.scrollTriggeredLoadPrev = false
    ctx.isLoadingNextVolume = false
    ctx.isLoadingPrevVolume = false
    ctx.isLoading = false
    ctx.allowAutoLoadAdjacentVolume = false

    if (typeof bridge.saveProgress === 'function') {
      bridge.saveProgress(true)
    }

    ctx.pagesFetchToken += 1
    if (ctx.observer) {
      ctx.observer.disconnect()
      ctx.observer = null
    }
    if (ctx.lastImageObserver) {
      ctx.lastImageObserver.disconnect()
      ctx.lastImageObserver = null
    }

    ctx.routeVolNum = String(volNum)
    ctx.currentDisplayVolNum = volNum
    ctx.currentProgressVolNum = volNum
    ctx.progressKey = `reading_progress_${ctx.comicId}_${volNum}`
    ctx.currentPageIndex = 0
    ctx.maxSavedPageIndex = -1
    ctx.nextVolumeNum = null
    ctx.nextVolumeVolId = null
    ctx.prevVolumeNum = null
    ctx.prevVolumeVolId = null

    applyLockedVolPreviewImages(ctx, payload)

    const vol = findVolByNum(ctx.volInfoVos, volNum)
    const volId = payload.volId || vol?.id
    if (!volId) return

    ctx.suppressVolumeSwitchOnce = true
    void ctx.$router.push({
      name: 'ComicReader',
      params: convertRouteParams({
        comicId: ctx.comicId,
        volNum,
      }),
    })

    openVolPay({
      comicId: String(ctx.comicId),
      volId: String(volId),
      volNum,
      onUnlocked: () => {
        ctx.pagesLocked = false
        ctx.isLoading = false
        if (typeof bridge.fetchComicPages === 'function') {
          void bridge.fetchComicPages(1, false, volNum)
        }
      },
    })
  }

  function getActiveVolNum() {
    if (typeof bridge.getActiveVolNum === 'function') {
      return bridge.getActiveVolNum()
    }
    return Number(ctx.$route?.params?.volNum ?? ctx.routeVolNum ?? ctx.volNum)
  }

  function isStaleVolumeSession(session, label, extra = {}) {
    if (session !== ctx.pagesFetchToken) {
      console.warn('[VolSwitch] 丢弃过期卷操作', {
        label,
        session,
        currentToken: ctx.pagesFetchToken,
        routeVol: ctx.$route?.params?.volNum,
        ctxVol: ctx.volNum,
        loadedVolumes: ctx.loadedVolumes?.map((v) => v.volNum) ?? [],
        ...extra,
      })
      return true
    }
    return false
  }

  function countVolumeImages(vol) {
    return vol.images?.length ?? 0
  }

  function sumLoadedVolumeImages() {
    return ctx.loadedVolumes.reduce((sum, vol) => sum + countVolumeImages(vol), 0)
  }

  function scheduleProgressTracking() {
    ctx.$nextTick(() => {
      if (typeof bridge.initProgressTracking === 'function') {
        bridge.initProgressTracking()
      }
    })
  }

  function shouldShowNextVolumeLoading() {
    if (ctx.showLoadNextVolumePrompt || ctx.showLoadPrevVolumePrompt) {
      return false
    }
    if (!ctx.isLoadingNextVolume && !ctx.isLoadingNextVolumeInteract) {
      return false
    }
    if (getCurrentVolumeImages().length > 0) {
      return false
    }
    const nextNum = ctx.nextVolumeNum
    if (nextNum != null) {
      const loaded = ctx.loadedVolumes.find((v) => v.volNum === nextNum && countVolumeImages(v) > 0)
      if (loaded) {
        return false
      }
    }
    return true
  }

  function getLoadingNextVolumeLabel() {
    if (ctx.nextVolumeNum != null) {
      return `第 ${ctx.nextVolumeNum} 卷加载中...`
    }
    const routeVol = getActiveVolNum()
    return `第 ${routeVol + ctx.loadedVolumes.length + 1} 卷加载中...`
  }

  async function checkAndLoadNextVolume() {
    if (ctx.isRestoringProgress) {
      return
    }
    if (!ctx.allowAutoLoadAdjacentVolume) {
            return
    }
    // 防止重复调用
    if (ctx.isLoadingNextVolume || ctx.isCheckingNextVolume) {
      return
    }

    ctx.isCheckingNextVolume = true

    try {
      // 计算当前已加载的最大卷号（必须用路由卷号，不能用滞后的 props）
      let maxLoadedVolNum = getActiveVolNum()

      // 检查已加载的卷
      if (ctx.loadedVolumes.length > 0) {
        const maxVol = ctx.loadedVolumes.reduce((max, vol) => {
          return vol.volNum > max.volNum ? vol : max
        }, ctx.loadedVolumes[0])
        maxLoadedVolNum = maxVol.volNum
      }

      // 检查是否还有下一卷
      const nextVolNum = maxLoadedVolNum + 1

      // 如果还有下一卷，且未加载，则加载
      if (nextVolNum <= ctx.totalVolumes) {
        // 检查是否已登录（第二卷及之后需要登录）
        if (isGuestBlockedVol(nextVolNum)) {
                    return
        }

        // 检查是否已经加载过
        const alreadyLoaded = ctx.loadedVolumes.find(v => v.volNum === nextVolNum)
        const isNextVolumeLoading = ctx.nextVolumeNum === nextVolNum

        if (!alreadyLoaded && !isNextVolumeLoading) {

          // 如果当前最大卷号等于初始卷号，说明是第一卷加载第二卷，使用 loadNextVolume
          if (maxLoadedVolNum === getActiveVolNum() && !ctx.hasNext && ctx.nextVolumePages.length === 0) {
                        await loadNextVolume()
          } else if (ctx.nextVolumeNum === null) {
            // 如果 nextVolumeNum 为 null，说明当前没有正在加载的卷，需要从已加载的卷加载下一卷
            // 临时设置 nextVolumeNum 为当前最大卷号，然后调用 loadNextVolumeAfterCurrent
                        const originalNextVolumeNum = ctx.nextVolumeNum
            ctx.nextVolumeNum = maxLoadedVolNum
            try {
              await loadNextVolumeAfterCurrent()
            } finally {
              // 如果 loadNextVolumeAfterCurrent 没有设置 nextVolumeNum，恢复原值
              // 但实际上 loadNextVolumeAfterCurrent 会设置 nextVolumeNum，所以这里不需要恢复
            }
          } else {
            // 否则使用 loadNextVolumeAfterCurrent
                        await loadNextVolumeAfterCurrent()
          }
        }
      }
    } catch (error) {
      console.error('❌ [checkAndLoadNextVolume] 加载下一卷失败:', error)
    } finally {
      ctx.isCheckingNextVolume = false
    }
  }

  function prevVolume() {
    if (ctx.volNum > 1) {
      // 切换前保存当前进度
      bridge.saveProgress(true)
    // 重置分页状态
    ctx.currentPageNum = 1
    ctx.pages = []
    ctx.lastLoadTriggerIndex = -1
    ctx.$router.push({
      name: 'ComicReader',
      params: convertRouteParams({ 
        comicId: ctx.comicId, 
        volNum: Number(ctx.volNum) - 1 
      })
    })
    }
  }

  function nextVolume() {
    // 检查用户是否登录
    const nextVolNum = Number(ctx.volNum) + 1
    if (isGuestBlockedVol(nextVolNum)) {
      ctx.$emit('show-login-popup')
      return
    }

    // 切换前保存当前进度
    bridge.saveProgress(true)
    // 重置分页状态
    ctx.currentPageNum = 1
    ctx.pages = []
    ctx.lastLoadTriggerIndex = -1
    ctx.$router.push({
      name: 'ComicReader',
      params: convertRouteParams({ 
        comicId: ctx.comicId, 
        volNum: Number(ctx.volNum) + 1 
      })
    })
  }

  async function loadNextVolumeAfterScroll() {
    const session = captureVolumeSession()

    // 加载提示会在动画结束后自动隐藏，这里保持显示直到加载完成

    const currentVolNum = getActiveVolNum()
    // 计算下一个应该加载的卷号：当前卷号 + 已加载的卷数 + 1
    const nextVolNum = currentVolNum + ctx.loadedVolumes.length + 1

    // 检查是否有下一卷
    if (nextVolNum > ctx.totalVolumes) {
            ctx.scrollTriggeredLoadNext = false
      ctx.$emit('show-popup-message', '已经是最后一卷了', 'login-required')
      return
    }

    // 检查用户是否登录（第二卷及之后需要登录）
    if (isGuestBlockedVol(nextVolNum)) {
            ctx.scrollTriggeredLoadNext = false
      ctx.$emit('show-login-popup')
      return
    }

    // 如果正在加载，避免重复请求
    if (ctx.isLoadingNextVolume) {
            ctx.scrollTriggeredLoadNext = false
      return
    }

    try {
            ctx.isLoadingNextVolume = true

      // 从 volInfoVos 中获取下一卷的 volId
      const nextVolInfo = ctx.volInfoVos.find(vol => vol.num === nextVolNum)
      if (nextVolInfo && nextVolInfo.id) {
        ctx.nextVolumeVolId = nextVolInfo.id
      } else {
        console.warn('⚠️ [loadNextVolumeAfterScroll] 未找到下一卷的 volId，volNum:', nextVolNum)
      }

      // 加载下一卷的第一页
      const response = await fetchVolPagesApi(
        {
          comicId: ctx.comicId,
          volNum: nextVolNum,
          pageNum: 1,
          pageSize: ctx.pageSize,
        },
        ctx.token
      )

      if (isStaleVolumeSession(session, 'loadNextVolumeAfterScroll:afterFetch', { nextVolNum })) {
        ctx.scrollTriggeredLoadNext = false
        return
      }

      if (response.data.code === 200 || response.data.code === 0) {
        const data = response.data.data || {}
        if (shouldSkipLockedAdjacentData(data)) {
          ctx.scrollTriggeredLoadNext = false
          enterLockedAdjacentVol(data, nextVolNum)
          return
        }
        const imgUrls = data.imgUrls || []

        // 保存下一卷的图片到 loadedVolumes（作为新的一卷）
        const startIndex = ctx.pages.length + (ctx.loadedVolumes.reduce((sum, vol) => sum + vol.images.length, 0))
        const newVolume = {
          volNum: nextVolNum,
          volId: ctx.nextVolumeVolId,
          startIndex: startIndex,
          endIndex: startIndex + imgUrls.length - 1,
          images: imgUrls,
          interactData: null,
          commentInput: ''
        }

        ctx.loadedVolumes.push(newVolume)
                ctx.nextVolumeCurrentPage = data.pageNum || 1
        ctx.nextVolumeHasNext = data.hasNext || false

        scheduleProgressTracking()

        // 如果下一卷有下一页，自动加载所有页面
        if (ctx.nextVolumeHasNext) {
          await loadNextVolumeAllPages(nextVolNum, ctx.nextVolumeVolId, newVolume, session)
          // 所有页面加载完成后，重置状态
          ctx.nextVolumeNum = null
          ctx.nextVolumeVolId = null
          ctx.nextVolumeCurrentPage = 1
          ctx.nextVolumeHasNext = false
        } else {
          // 如果没有下一页，立即获取互动数据
          await fetchLoadedVolumeInteractData(newVolume)
          // 卷加载完成，重置状态和滚动触发标志，允许加载下一卷
          ctx.nextVolumeNum = null
          ctx.nextVolumeVolId = null
          ctx.nextVolumeCurrentPage = 1
          ctx.nextVolumeHasNext = false
          ctx.scrollTriggeredLoadNext = false
          scheduleProgressTracking()
        }
      } else {
        handleDeniedAdjacentVolPages(response.data.code)
        if (resolveVolPagesResult(response.data.code) === 'need-pay') {
          enterLockedAdjacentVol(response.data?.data || {}, nextVolNum)
        }
        ctx.scrollTriggeredLoadNext = false
      }
    } catch (error) {
      console.error('❌ [loadNextVolumeAfterScroll] 请求失败:', error)
      ctx.scrollTriggeredLoadNext = false
    } finally {
      ctx.isLoadingNextVolume = false
    }
  }

  async function loadNextVolumeAllPages(volNum, volId, volumeObj, session = captureVolumeSession()) {
    let currentPage = 2 // 从第二页开始（第一页已加载）

    while (true) {
      if (isStaleVolumeSession(session, 'loadNextVolumeAllPages:loop', { volNum })) {
        break
      }
      try {
        const response = await fetchVolPagesApi(
          {
            comicId: ctx.comicId,
            volNum: volNum,
            pageNum: currentPage,
            pageSize: ctx.pageSize,
          },
          ctx.token
        )

        if (isStaleVolumeSession(session, 'loadNextVolumeAllPages:afterFetch', { volNum, currentPage })) {
          break
        }

        if (response.data.code === 200 || response.data.code === 0) {
          const data = response.data.data || {}
          if (shouldSkipLockedAdjacentData(data)) {
            ctx.scrollTriggeredLoadNext = false
            break
          }
          const imgUrls = data.imgUrls || []

          // 追加图片到当前卷
          volumeObj.images = [...volumeObj.images, ...imgUrls]
          volumeObj.endIndex = volumeObj.startIndex + volumeObj.images.length - 1

          const hasNext = data.hasNext || false

          if (!hasNext) {
            // 所有页面加载完成，获取互动数据
            if (!isStaleVolumeSession(session, 'loadNextVolumeAllPages:beforeInteract', { volNum })) {
              await fetchLoadedVolumeInteractData(volumeObj)
            }
            // 卷加载完成，重置状态和滚动触发标志，允许加载下一卷
            ctx.nextVolumeCurrentPage = 1
            ctx.nextVolumeHasNext = false
          ctx.scrollTriggeredLoadNext = false
                    scheduleProgressTracking()
          break
          }

          currentPage++
        } else {
          handleDeniedAdjacentVolPages(response.data.code, volNum)
          break
        }
      } catch (error) {
        console.error('❌ [loadNextVolumeAllPages] 请求失败:', error)
        break
      }
    }
  }

  async function fetchLoadedVolumeInteractData(volumeObj) {
    if (!volumeObj.volId) {
      console.warn('⚠️ [fetchLoadedVolumeInteractData] volId 为空，无法获取互动数据')
      return
    }
    if (!volumeObj.images?.length) {
      console.warn('⚠️ [fetchLoadedVolumeInteractData] 画页未加载，跳过互动区，卷号:', volumeObj.volNum)
      return
    }

    try {
      const response = await fetchVolInteractApi(volumeObj.volId, ctx.token)

      if (response.data.code === 200 || response.data.code === 0) {
        volumeObj.interactData = normalizeVolInteractData(response.data.data || null)
              } else {
        console.error('❌ [fetchLoadedVolumeInteractData] 获取互动数据失败:', response.data)
      }
    } catch (error) {
      console.error('❌ [fetchLoadedVolumeInteractData] 请求失败:', error)
    }
  }

  async function loadPrevVolumeAfterScroll() {

    const currentVolNum = getActiveVolNum()
    // 计算上一个应该加载的卷号
    // 如果已加载了上一卷，找到最小的卷号，然后减1；否则就是当前卷号减1
    let prevVolNum
    if (ctx.loadedPrevVolumes.length > 0) {
      const minLoadedVolNum = Math.min(...ctx.loadedPrevVolumes.map(vol => vol.volNum))
      prevVolNum = minLoadedVolNum - 1
    } else {
      prevVolNum = currentVolNum - 1
    }

    // 检查是否有上一卷
    if (prevVolNum < 1) {
            ctx.scrollTriggeredLoadPrev = false
      ctx.$emit('show-popup-message', '已经是第一卷了', 'login-required')
      return
    }

    // 检查用户是否登录（第二卷及之后需要登录）
    if (isGuestBlockedVol(prevVolNum)) {
            ctx.scrollTriggeredLoadPrev = false
      ctx.$emit('show-login-popup')
      return
    }

    // 如果正在加载，避免重复请求
    if (ctx.isLoadingPrevVolume) {
            ctx.scrollTriggeredLoadPrev = false
      return
    }

    try {
            ctx.isLoadingPrevVolume = true
      ctx.prevVolumeNum = prevVolNum

      // 从 volInfoVos 中获取上一卷的 volId
      const prevVolInfo = ctx.volInfoVos.find(vol => vol.num === prevVolNum)
      if (prevVolInfo && prevVolInfo.id) {
        ctx.prevVolumeVolId = prevVolInfo.id
      } else {
        console.warn('⚠️ [loadPrevVolumeAfterScroll] 未找到上一卷的 volId，volNum:', prevVolNum)
      }

      // 加载上一卷的第一页
      const response = await fetchVolPagesApi(
        {
          comicId: ctx.comicId,
          volNum: prevVolNum,
          pageNum: 1,
          pageSize: ctx.pageSize,
        },
        ctx.token
      )

      if (response.data.code === 200 || response.data.code === 0) {
        const data = response.data.data || {}
        if (shouldSkipLockedAdjacentData(data)) {
          ctx.scrollTriggeredLoadPrev = false
          enterLockedAdjacentVol(data, prevVolNum)
          return
        }
        const imgUrls = data.imgUrls || []

        // 保存上一卷的图片到 loadedPrevVolumes（作为新的一卷，插入到数组开头）
        // 上一卷使用负数索引，从-1开始，倒序排列
        const prevVolumeImagesCount = ctx.loadedPrevVolumes.reduce((sum, vol) => sum + vol.images.length, 0)
        const startIndex = -(prevVolumeImagesCount + imgUrls.length) // 负索引，表示在当前卷之前
        const newVolume = {
          volNum: prevVolNum,
          volId: ctx.prevVolumeVolId,
          startIndex: startIndex,
          endIndex: startIndex + imgUrls.length - 1,
          images: imgUrls,
          interactData: null,
          commentInput: ''
        }

        ctx.loadedPrevVolumes.unshift(newVolume) // 插入到数组开头，保持倒序（最新的上一卷在最前面）
        ctx.prevVolumeCurrentPage = data.pageNum || 1
        ctx.prevVolumeHasNext = data.hasNext || false

        // 如果上一卷有下一页，自动加载所有页面
        if (ctx.prevVolumeHasNext) {
          await loadPrevVolumeAllPages(prevVolNum, ctx.prevVolumeVolId, newVolume)
          // 所有页面加载完成后，重置状态
          ctx.prevVolumeNum = null
          ctx.prevVolumeVolId = null
          ctx.prevVolumeCurrentPage = 1
          ctx.prevVolumeHasNext = false
        } else {
          // 如果没有下一页，立即获取互动数据
          await fetchLoadedVolumeInteractData(newVolume)
          // 卷加载完成，重置状态和滚动触发标志，允许加载上一卷
          ctx.prevVolumeNum = null
          ctx.prevVolumeVolId = null
          ctx.prevVolumeCurrentPage = 1
          ctx.prevVolumeHasNext = false
          ctx.scrollTriggeredLoadPrev = false
        }
      } else {
        handleDeniedAdjacentVolPages(response.data.code)
        if (resolveVolPagesResult(response.data.code) === 'need-pay') {
          enterLockedAdjacentVol(response.data?.data || {}, prevVolNum)
        }
        ctx.scrollTriggeredLoadPrev = false
      }
    } catch (error) {
      console.error('❌ [loadPrevVolumeAfterScroll] 请求失败:', error)
      ctx.scrollTriggeredLoadPrev = false
    } finally {
      ctx.isLoadingPrevVolume = false
    }
  }

  async function loadPrevVolumeAllPages(volNum, volId, volumeObj) {
    let currentPage = 2 // 从第二页开始（第一页已加载）

    while (true) {
      try {
        const response = await fetchVolPagesApi(
          {
            comicId: ctx.comicId,
            volNum: volNum,
            pageNum: currentPage,
            pageSize: ctx.pageSize,
          },
          ctx.token
        )

        if (response.data.code === 200 || response.data.code === 0) {
          const data = response.data.data || {}
          if (shouldSkipLockedAdjacentData(data)) {
            ctx.scrollTriggeredLoadPrev = false
            break
          }
          const imgUrls = data.imgUrls || []

          // 追加图片到当前卷
          volumeObj.images = [...volumeObj.images, ...imgUrls]
          // 更新结束索引
          volumeObj.endIndex = volumeObj.startIndex + volumeObj.images.length - 1

          const hasNext = data.hasNext || false

          if (!hasNext) {
            // 所有页面加载完成，获取互动数据
            await fetchLoadedVolumeInteractData(volumeObj)
            // 卷加载完成，重置状态和滚动触发标志，允许加载上一卷
            ctx.prevVolumeCurrentPage = 1
            ctx.prevVolumeHasNext = false
            ctx.scrollTriggeredLoadPrev = false
                        break
          }

          currentPage++
        } else {
          handleDeniedAdjacentVolPages(response.data.code, volNum)
          break
        }
      } catch (error) {
        console.error('❌ [loadPrevVolumeAllPages] 请求失败:', error)
        break
      }
    }
  }

  async function loadNextVolume() {
    const currentVolNum = getActiveVolNum()
    const nextVolNum = currentVolNum + 1

    // 检查是否有下一卷
    if (nextVolNum > ctx.totalVolumes) {
            return
    }

    // 检查用户是否登录（第二卷及之后需要登录）
    if (isGuestBlockedVol(nextVolNum)) {
            return
    }

    // 如果正在加载，避免重复请求
    if (ctx.isLoadingNextVolume) {
            return
    }

    try {
            ctx.isLoadingNextVolume = true
      ctx.nextVolumeNum = nextVolNum

      // 从 volInfoVos 中获取下一卷的 volId
      const nextVolInfo = ctx.volInfoVos.find(vol => vol.num === nextVolNum)
      if (nextVolInfo && nextVolInfo.id) {
        ctx.nextVolumeVolId = nextVolInfo.id
      }

      // 加载下一卷的第一页
      const response = await fetchVolPagesApi(
        {
          comicId: ctx.comicId,
          volNum: nextVolNum,
          pageNum: 1,
          pageSize: ctx.pageSize,
        },
        ctx.token
      )

      if (response.data.code === 200 || response.data.code === 0) {
        const data = response.data.data || {}
        if (shouldSkipLockedAdjacentData(data)) {
          ctx.nextVolumeNum = null
          ctx.nextVolumeVolId = null
          ctx.nextVolumePages = []
          ctx.nextVolumeHasNext = false
          return
        }
        const imgUrls = data.imgUrls || []

        // 保存下一卷的图片
        ctx.nextVolumePages = imgUrls
        // 更新下一卷的分页信息
        ctx.nextVolumeCurrentPage = data.pageNum || 1
        ctx.nextVolumeHasNext = data.hasNext || false

        // 如果下一卷有下一页，自动加载下一页
        if (ctx.nextVolumeHasNext) {
                    // 延迟一点时间，确保 isLoadingNextVolume 已重置（在 finally 块之后）
          setTimeout(() => {
            if (ctx.nextVolumeHasNext && !ctx.isLoadingNextVolume && ctx.nextVolumeNum) {
                            loadNextVolumeNextPage()
            }
          }, 800)
        } else if (!ctx.nextVolumeHasNext) {
          // 如果下一卷没有下一页，立即获取下一卷的互动数据
          if (!ctx.isLoadingNextVolumeInteract && !ctx.nextVolumeInteractData) {
                        getNextVolumeVolIdAndFetchInteract().then(() => {
                          })
          }
        }

        // 等待 DOM 更新后初始化进度追踪
        scheduleProgressTracking()
      } else {
        handleDeniedAdjacentVolPages(response.data.code, nextVolNum)
      }
    } catch (error) {
      console.error('请求下一卷内容失败:', error)
    } finally {
      ctx.isLoadingNextVolume = false
    }
  }

  async function loadNextVolumeNextPage() {
    if (!ctx.nextVolumeHasNext || ctx.isLoadingNextVolume || !ctx.nextVolumeNum) {
      return
    }

    const session = captureVolumeSession()

    try {
            ctx.isLoadingNextVolume = true

      const nextPageNum = ctx.nextVolumeCurrentPage + 1
      const response = await fetchVolPagesApi(
        {
          comicId: ctx.comicId,
          volNum: ctx.nextVolumeNum,
          pageNum: nextPageNum,
          pageSize: ctx.pageSize,
        },
        ctx.token
      )

      if (isStaleVolumeSession(session, 'loadNextVolumeNextPage:afterFetch', { volNum: ctx.nextVolumeNum })) {
        return
      }

      if (response.data.code === 200 || response.data.code === 0) {
        const data = response.data.data || {}
        if (shouldSkipLockedAdjacentData(data)) {
          ctx.nextVolumeHasNext = false
          return
        }
        const imgUrls = data.imgUrls || []

        // 追加下一卷的图片
        ctx.nextVolumePages = [...ctx.nextVolumePages, ...imgUrls]
        // 更新下一卷的分页信息
        ctx.nextVolumeCurrentPage = data.pageNum || nextPageNum
        ctx.nextVolumeHasNext = data.hasNext || false

        // 如果下一卷没有下一页，立即获取下一卷的互动数据
        if (!ctx.nextVolumeHasNext && !ctx.isLoadingNextVolumeInteract && !ctx.nextVolumeInteractData) {
                    // 等待互动数据加载完成后再处理
          getNextVolumeVolIdAndFetchInteract().then(() => {
            // 互动数据加载完成后，会由 fetchNextVolumeInteractData 处理保存和后续加载
          }).catch((error) => {
            console.error('获取下一卷互动数据失败:', error)
            // 即使获取互动数据失败，也应该继续加载下一卷
            setTimeout(() => {
              checkAndLoadNextVolume()
            }, 600)
          })
        }

        // 如果下一卷没有下一页，且互动数据已经存在，将其保存到 loadedVolumes 中
        if (!ctx.nextVolumeHasNext && ctx.nextVolumeInteractData) {
          const currentVol = ctx.loadedVolumes.find(v => v.volNum === ctx.nextVolumeNum)
          if (!currentVol && !isStaleVolumeSession(session, 'loadNextVolumeNextPage:beforePush', { volNum: ctx.nextVolumeNum })) {
            const startIndex = ctx.pages.length + sumLoadedVolumeImages()
            const prevImagesCount = sumLoadedVolumeImages()
            const currentVolImages = ctx.nextVolumePages.slice(prevImagesCount)

            if (currentVolImages.length === 0) {
              console.warn('[VolSwitch] loadNextVolumeNextPage 跳过：无图片数据，卷号:', ctx.nextVolumeNum)
            } else {
            ctx.loadedVolumes.push({
              volNum: ctx.nextVolumeNum,
              volId: ctx.nextVolumeVolId,
              startIndex: startIndex,
              endIndex: startIndex + currentVolImages.length - 1,
              interactData: ctx.nextVolumeInteractData,
              commentInput: '',
              images: currentVolImages
            })
                                    }
          }

          // 下一卷加载完成，不再自动加载下一卷，等待用户看到一半时再加载
                  } else if (!ctx.nextVolumeHasNext) {
          // 如果下一卷没有下一页但没有互动数据，不再自动加载下一卷
                  }

        // 等待 DOM 更新后重新初始化进度追踪
        scheduleProgressTracking()
      } else {
        handleDeniedAdjacentVolPages(response.data.code, ctx.nextVolumeNum)
      }
    } catch (error) {
      console.error('请求下一卷下一页失败:', error)
    } finally {
      ctx.isLoadingNextVolume = false
    }
  }

  async function getNextVolumeVolIdAndFetchInteract() {
    if (!ctx.nextVolumeNum) {
      return Promise.resolve()
    }

    // 从 volInfoVos 中获取下一卷的 volId
    const nextVolInfo = ctx.volInfoVos.find(vol => vol.num === ctx.nextVolumeNum)
    if (nextVolInfo && nextVolInfo.id) {
      ctx.nextVolumeVolId = nextVolInfo.id
            await fetchNextVolumeInteractData()
    } else {
      console.warn('未找到下一卷的 volId，卷号:', ctx.nextVolumeNum)
    }
  }

  async function fetchNextVolumeInteractData() {
    if (!ctx.nextVolumeVolId || ctx.isLoadingNextVolumeInteract) {
            return
    }

    const session = captureVolumeSession()

    try {
            ctx.isLoadingNextVolumeInteract = true
      const response = await fetchVolInteractApi(ctx.nextVolumeVolId, ctx.token)

      if (isStaleVolumeSession(session, 'fetchNextVolumeInteractData:afterFetch', { volNum: ctx.nextVolumeNum })) {
        return
      }

      if (response.data.code === 200 || response.data.code === 0) {
        ctx.nextVolumeInteractData = normalizeVolInteractData(response.data.data || null)

        // 如果下一卷没有下一页，将其保存到 loadedVolumes 中
        if (!ctx.nextVolumeHasNext && ctx.nextVolumeInteractData) {
          const currentVol = ctx.loadedVolumes.find(v => v.volNum === ctx.nextVolumeNum)
          if (!currentVol && !isStaleVolumeSession(session, 'fetchNextVolumeInteractData:beforePush', { volNum: ctx.nextVolumeNum })) {
            // 计算当前卷在 nextVolumePages 中的起始和结束索引
            let startIndex = ctx.pages.length + sumLoadedVolumeImages()
            const prevImagesCount = sumLoadedVolumeImages()
            const currentVolImages = ctx.nextVolumePages.slice(prevImagesCount)

            if (currentVolImages.length === 0) {
              console.warn('[VolSwitch] fetchNextVolumeInteractData 跳过：无图片数据，卷号:', ctx.nextVolumeNum)
              return
            }

            ctx.loadedVolumes.push({
              volNum: ctx.nextVolumeNum,
              volId: ctx.nextVolumeVolId,
              startIndex: startIndex,
              endIndex: startIndex + currentVolImages.length - 1,
              interactData: ctx.nextVolumeInteractData,
              commentInput: '',
              images: currentVolImages // 保存当前卷的图片列表
            })

            // 保存当前卷号，用于日志
            const savedVolNum = ctx.nextVolumeNum

            // 保存完成后，清空 nextVolumePages 和 nextVolumeNum，确保后续滚动时进入"已完成的卷检查"逻辑
            ctx.nextVolumePages = []
            ctx.nextVolumeNum = null
            ctx.nextVolumeVolId = null
            ctx.nextVolumeCurrentPage = 1
            ctx.nextVolumeHasNext = false
            ctx.nextVolumeInteractData = null
            ctx.nextVolumeCommentInput = ''

            // 保存完成后，等待 DOM 更新，然后更新最后一个图片的监听器
            ctx.$nextTick(() => {
              // 更新最后一个图片的监听器（会触发 loadedVolumes 的 watch）
              bridge.setupLastImageObserver()
            })

            // 保存完成后，等待用户滚动到最后一个图片时自动加载下一卷
                      } else {
            // 如果已经存在，不再自动加载下一卷
                      }
        } else if (!ctx.nextVolumeHasNext && !ctx.nextVolumeInteractData) {
          // 如果下一卷没有下一页，但没有互动数据，不再自动加载下一卷
                  }
      } else {
        console.error('获取下一卷互动数据失败:', response.data)
      }
    } catch (error) {
      console.error('请求下一卷互动数据失败:', error)
    } finally {
      ctx.isLoadingNextVolumeInteract = false
          }
  }

  async function loadNextVolumeAfterCurrent() {
    if (!ctx.nextVolumeNum) {
            return
    }

    const session = captureVolumeSession()

    const nextVolNum = ctx.nextVolumeNum + 1

    // 检查是否还有下一卷
    if (nextVolNum > ctx.totalVolumes) {
            return
    }

    // 检查用户是否登录（第二卷及之后需要登录）
    if (isGuestBlockedVol(nextVolNum)) {
            return
    }

    // 如果正在加载，避免重复请求
    if (ctx.isLoadingNextVolume) {
            return
    }

    // 检查是否已经加载过这一卷（避免重复加载）
    const alreadyLoaded = ctx.loadedVolumes.find(v => v.volNum === nextVolNum)
    if (alreadyLoaded) {
            return
    }

    try {
            ctx.isLoadingNextVolume = true

      // 从 volInfoVos 中获取下一卷的 volId
      const nextVolInfo = ctx.volInfoVos.find(vol => vol.num === nextVolNum)
      let nextVolId = null
      if (nextVolInfo && nextVolInfo.id) {
        nextVolId = nextVolInfo.id
      }

      // 加载下一卷的第一页
      const response = await fetchVolPagesApi(
        {
          comicId: ctx.comicId,
          volNum: nextVolNum,
          pageNum: 1,
          pageSize: ctx.pageSize,
        },
        ctx.token
      )

      if (isStaleVolumeSession(session, 'loadNextVolumeAfterCurrent:afterFetch', { nextVolNum })) {
        return
      }

      if (response.data.code === 200 || response.data.code === 0) {
        const data = response.data.data || {}
        if (shouldSkipLockedAdjacentData(data)) {
          ctx.nextVolumeHasNext = false
          return
        }
        const imgUrls = data.imgUrls || []

        // 在更新 nextVolumeNum 之前，先保存当前卷的互动数据到 loadedVolumes（如果当前卷已完成）
        const prevVolNum = ctx.nextVolumeNum
        if (!ctx.nextVolumeHasNext && ctx.nextVolumeInteractData) {
          const prevVol = ctx.loadedVolumes.find(v => v.volNum === prevVolNum)
          if (!prevVol && !isStaleVolumeSession(session, 'loadNextVolumeAfterCurrent:beforePushPrev', { prevVolNum })) {
            // 计算当前卷在 nextVolumePages 中的起始和结束索引
            let startIndex = ctx.pages.length
            // 计算之前已完成的卷占用的图片数量
            for (const vol of ctx.loadedVolumes) {
              startIndex += (vol.images ? vol.images.length : (vol.endIndex - vol.startIndex + 1))
            }
            // 当前卷的图片数量（在追加新卷之前）
            const prevImagesCount = sumLoadedVolumeImages()
            const currentVolImages = ctx.nextVolumePages.slice(prevImagesCount)

            if (currentVolImages.length > 0) {
              ctx.loadedVolumes.push({
                volNum: prevVolNum,
                volId: ctx.nextVolumeVolId,
                startIndex: startIndex,
                endIndex: startIndex + currentVolImages.length - 1,
                interactData: ctx.nextVolumeInteractData,
                commentInput: ctx.nextVolumeCommentInput,
                images: currentVolImages
              })
                          } else {
              console.warn('[VolSwitch] loadNextVolumeAfterCurrent 跳过 prev 卷 push：无图片，卷号:', prevVolNum)
            }
          }
        }

        // 将新卷的图片追加到下一卷的图片列表中
        ctx.nextVolumePages = [...ctx.nextVolumePages, ...imgUrls]

        // 更新下一卷的分页信息（新卷作为新的下一卷）
        ctx.nextVolumeNum = nextVolNum
        ctx.nextVolumeVolId = nextVolId
        ctx.nextVolumeCurrentPage = data.pageNum || 1
        ctx.nextVolumeHasNext = data.hasNext || false
        ctx.nextVolumeLastLoadTriggerIndex = -1 // 重置触发索引
        // 重置下一卷的互动数据（新卷的互动数据会在加载完成后获取）
        ctx.nextVolumeInteractData = null
        ctx.nextVolumeCommentInput = ''

        // 如果新卷有下一页，自动加载下一页
        if (ctx.nextVolumeHasNext) {
                    // 延迟一点时间，确保 isLoadingNextVolume 已重置（在 finally 块之后）
          setTimeout(() => {
            if (ctx.nextVolumeHasNext && !ctx.isLoadingNextVolume && ctx.nextVolumeNum) {
                            loadNextVolumeNextPage()
            }
          }, 800)
        } else if (!ctx.nextVolumeHasNext) {
          // 如果新卷没有下一页，立即获取新卷的互动数据
          if (!ctx.isLoadingNextVolumeInteract && !ctx.nextVolumeInteractData) {
                        getNextVolumeVolIdAndFetchInteract().then(() => {
                          })
          } else {
            // 如果互动数据已经存在，不再自动加载下一卷
                      }
        }

        // 等待 DOM 更新后初始化进度追踪
        scheduleProgressTracking()
      } else {
        handleDeniedAdjacentVolPages(response.data.code, nextVolNum)
      }
    } catch (error) {
      console.error('请求下一卷内容失败:', error)
    } finally {
      ctx.isLoadingNextVolume = false
    }
  }

  function setNextVolumeImageRef(el, index) {
    if (el) {
      const globalIndex = ctx.pages.length + getCurrentVolumeStartIndex() + index
      ctx.imageRefs[globalIndex] = el
    }
  }

  function getCurrentVolumeImages() {
    if (ctx.loadedVolumes.length === 0) {
      return ctx.nextVolumePages
    }
    const loadedImagesCount = sumLoadedVolumeImages()
    return ctx.nextVolumePages.slice(loadedImagesCount)
  }

  function getCurrentVolumeStartIndex() {
    if (ctx.loadedVolumes.length === 0) {
      return 0
    }
    return sumLoadedVolumeImages()
  }

  Object.assign(bridge, {
    checkAndLoadNextVolume,
    prevVolume,
    nextVolume,
    loadNextVolumeAfterScroll,
    loadNextVolumeAllPages,
    fetchLoadedVolumeInteractData,
    loadPrevVolumeAfterScroll,
    loadPrevVolumeAllPages,
    loadNextVolume,
    loadNextVolumeNextPage,
    getNextVolumeVolIdAndFetchInteract,
    fetchNextVolumeInteractData,
    loadNextVolumeAfterCurrent,
    setNextVolumeImageRef,
    getCurrentVolumeImages,
    getCurrentVolumeStartIndex,
    shouldShowNextVolumeLoading,
    getLoadingNextVolumeLabel,
  })
}
